import { __awaiter, __rest } from "tslib";
import { getOauthClientId } from '@local/app-config';
import { fetchBaseQuery, } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { DEV_HUB_URL_STRING, GLOBAL_DISCOVERY_DEVELOPMENT_URL, GLOBAL_DISCOVERY_INTEGRATION_URL, LOGIN_SILENT, LOGIN_SUCCESS_ROUTE, } from '../../../constants';
import { AUTH_CODE_KEY, getCombinedToken as getAccessToken, setCombinedToken as setAccessToken, } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getPkceVerifier } from '../../../store/sessionStorageHelpers/pkceFlowHelpers';
import { BentleyIdentityApiClient } from '../BentleyIdentityApiClient';
import { isFetchArgs, mergeResults } from './BentleyAccessTokenApiUtils';
export function accessTokenBody(code, forceOauthPrefix, tabId, extendSession) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const redirectUrl = extendSession
        ? `${window.location.origin}${LOGIN_SILENT}`
        : `${window.location.origin}${LOGIN_SUCCESS_ROUTE}`;
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('code', code);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', redirectUrl);
    urlEncodedBody.append('client_id', clientId);
    const verifier = getPkceVerifier(tabId);
    urlEncodedBody.append('code_verifier', verifier);
    return urlEncodedBody;
}
export function refreshTokenBody(refreshToken, forceOauthPrefix) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('grant_type', 'refresh_token');
    urlEncodedBody.append('refresh_token', refreshToken);
    urlEncodedBody.append('client_id', clientId);
    return urlEncodedBody;
}
const tokenMutex = new Mutex();
export function processResult(currentArgs, WebApi, extraOptions, forceOauthPrefix, url, discoveryIntegrationUrl, hubUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const rawBaseQuery = fetchBaseQuery({
            baseUrl: url,
            prepareHeaders: (headers) => {
                var _a;
                const token = (_a = getAccessToken()) === null || _a === void 0 ? void 0 : _a.access_token;
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                }
                return headers;
            },
        });
        const rawBaseQueryIntegration = discoveryIntegrationUrl
            ? fetchBaseQuery({
                baseUrl: discoveryIntegrationUrl,
                prepareHeaders: (headers) => {
                    var _a;
                    const token = (_a = getAccessToken()) === null || _a === void 0 ? void 0 : _a.access_token;
                    if (token) {
                        headers.set('authorization', `Bearer ${token}`);
                    }
                    return headers;
                },
            })
            : undefined;
        yield tokenMutex.waitForUnlock();
        let result = rawBaseQueryIntegration
            ? yield mergeResults(currentArgs, WebApi, extraOptions, rawBaseQuery, rawBaseQueryIntegration, hubUrl)
            : yield rawBaseQuery(currentArgs, WebApi, extraOptions);
        const originalToken = getAccessToken();
        if (result.error && result.error.status === 401 && originalToken) {
            const refreshToken = (_a = originalToken === null || originalToken === void 0 ? void 0 : originalToken.refresh_token) !== null && _a !== void 0 ? _a : false;
            if (!refreshToken) {
                return result;
            }
            if (!tokenMutex.isLocked()) {
                const refreshResult = yield WebApi.dispatch(AccessTokenApi.endpoints.refreshBentleyAccessToken.initiate({
                    forceOauthPrefix,
                    refreshToken,
                }, { subscribe: true, forceRefetch: true }));
                const newToken = getAccessToken();
                if ((refreshResult === null || refreshResult === void 0 ? void 0 : refreshResult.isSuccess) && originalToken.access_token !== (newToken === null || newToken === void 0 ? void 0 : newToken.access_token)) {
                    result = rawBaseQueryIntegration
                        ? yield mergeResults(currentArgs, WebApi, extraOptions, rawBaseQuery, rawBaseQueryIntegration, hubUrl)
                        : yield rawBaseQuery(currentArgs, WebApi, extraOptions);
                }
            }
            else {
                yield tokenMutex.waitForUnlock();
                result = rawBaseQueryIntegration
                    ? yield mergeResults(currentArgs, WebApi, extraOptions, rawBaseQuery, rawBaseQueryIntegration, hubUrl)
                    : yield rawBaseQuery(currentArgs, WebApi, extraOptions);
            }
        }
        return result;
    });
}
export const AccessTokenApi = BentleyIdentityApiClient.injectEndpoints({
    endpoints: (build) => ({
        retrieveBentleyAccessToken: build.query({
            queryFn(_a, _api_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ authCode, forceOauthPrefix, tabId, extendSession }, _api, _extraOptions, baseQuery) {
                    const accessTokenResponse = yield baseQuery({
                        url: 'connect/token',
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: accessTokenBody(authCode, forceOauthPrefix, tabId, extendSession),
                    });
                    if (accessTokenResponse.error) {
                        return { error: accessTokenResponse.error };
                    }
                    setAccessToken(accessTokenResponse.data);
                    sessionStorage.setItem(AUTH_CODE_KEY, authCode);
                    return { data: accessTokenResponse.data };
                });
            },
        }),
        refreshBentleyAccessToken: build.query({
            queryFn(_a, _api_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ forceOauthPrefix, refreshToken }, _api, _extraOptions, baseQuery) {
                    const accessTokenResponse = yield baseQuery({
                        url: 'connect/token',
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: refreshTokenBody(refreshToken, forceOauthPrefix),
                    });
                    if (accessTokenResponse.error) {
                        return { error: accessTokenResponse.error };
                    }
                    setAccessToken(accessTokenResponse.data);
                    return { data: accessTokenResponse.data };
                });
            },
        }),
    }),
});
export function createBentleyAccessTokenBaseQuery(urlConfig, transformedFilterParams) {
    return (args, WebApi, extraOptions) => __awaiter(this, void 0, void 0, function* () {
        const urlConfiguration = urlConfig();
        if (urlConfiguration === null || !urlConfiguration.url) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }
        const { url: baseUrl, forceOauthPrefix } = urlConfiguration;
        let currentArgs;
        if (transformedFilterParams) {
            currentArgs = transformedFilterParams(args);
        }
        else {
            currentArgs = args;
        }
        let hubUrl = '';
        let combineResponses = false;
        if (isFetchArgs(currentArgs) && 'params' in currentArgs && currentArgs.params) {
            const _a = currentArgs.params, { combineResponses: cr, hubUrl: hu } = _a, restParams = __rest(_a, ["combineResponses", "hubUrl"]);
            combineResponses = !!cr;
            hubUrl = hu !== null && hu !== void 0 ? hu : '';
            currentArgs = Object.assign(Object.assign({}, currentArgs), { params: Object.keys(restParams).length > 0 ? restParams : undefined });
        }
        if (combineResponses) {
            let developmentDiscoveryUrl, integrationDiscoveryUrl;
            if (baseUrl.includes(DEV_HUB_URL_STRING) ||
                baseUrl === GLOBAL_DISCOVERY_DEVELOPMENT_URL) {
                developmentDiscoveryUrl = baseUrl;
                integrationDiscoveryUrl = GLOBAL_DISCOVERY_INTEGRATION_URL;
            }
            else {
                developmentDiscoveryUrl = GLOBAL_DISCOVERY_DEVELOPMENT_URL;
                integrationDiscoveryUrl = baseUrl;
            }
            return processResult(currentArgs, WebApi, extraOptions, forceOauthPrefix, developmentDiscoveryUrl, integrationDiscoveryUrl, hubUrl);
        }
        return processResult(currentArgs, WebApi, extraOptions, forceOauthPrefix, baseUrl);
    });
}
export const { useRetrieveBentleyAccessTokenQuery, useLazyRetrieveBentleyAccessTokenQuery, useLazyRefreshBentleyAccessTokenQuery, } = AccessTokenApi;
//# sourceMappingURL=BentleyAccessTokenApi.js.map